/**
 * TrendLineChart · Module A 投放趋势对比 - 折线图
 *
 * 需求 §4.4.4：
 * - 每条线代表一款游戏
 * - X 轴日期，Y 轴指标数值
 * - 配色 chartAllColors
 * - tooltip 用 formatChartParamsDate（带游戏 icon）
 * - 图例带游戏 icon + 名称，点击可显隐对应游戏
 */
import React, { useMemo } from 'react';
import EChartCommon from '@op/components/EChartCommon';
import {
  formatChartParamsDate,
} from '@op/components/ChartCommon/formatter';
import { DBO_EC, DBO_TM, DBO_V } from '@op/utils/format';
import { ITrendSeries } from '../../types/trend';
import { TDateType } from '../../types/common';
import { TGameMap } from '../../hooks/useGameMap';
import { useGameColorContext } from '../../context/GameColorContext';
import {
  buildEchartSeries,
  buildGameRich,
  buildXAxisData,
  gameRichLabel,
  gameTooltipLabel,
} from './utils';

export interface ITrendLineChartProps {
  series: ITrendSeries[];
  /** 游戏元信息映射（cond.materials_config_games） */
  gameMap: TGameMap;
  /** 时间粒度（用于 tooltip 标题格式化） */
  dateType: TDateType;
  /** 图表高度 */
  height?: number;
  emptyTitleText: string;
}

export default function TrendLineChart({
  series,
  gameMap,
  dateType,
  height = 360,
  emptyTitleText,
}: ITrendLineChartProps) {

  const { gameColorMap } = useGameColorContext();

  const empty = useMemo(() => {
    return series.every((item) => item.values?.length === 0);
  }, [series]);

  const options = useMemo(() => {
    const xAxisData = buildXAxisData(series);
    const echartSeries = buildEchartSeries(series, 'line', gameColorMap);
    const gameRich = buildGameRich(series, gameMap, 16);

    // 图例顺序：按第一个时间点的游戏值从大到小排列
    const firstTimeValues = new Map<string, number>();
    series.forEach((s) => {
      const firstValue = s.values?.[0]?.value;
      if (firstValue !== undefined) {
        firstTimeValues.set(s.game_id, firstValue);
      }
    });
    const legendData = series
      .map((s) => s.game_id)
      .sort((a, b) => (firstTimeValues.get(b) || 0) - (firstTimeValues.get(a) || 0));

    return {
      legend: {
        data: legendData,
        formatter: (gameId: string) => gameRichLabel(gameId, gameMap),
        textStyle: {
          rich: gameRich,
        },
      },
      tooltip: {
        formatter: (params: any) =>
          formatChartParamsDate({
            title: DBO_EC.toolTime(params?.[0]?.axisValue, dateType),
            params,
            isSort: true,
            listTitleFunc: (item: any) => gameTooltipLabel(item.seriesName, gameMap),
            valueFormat: (val: number) =>
              Number.isFinite(val) ? DBO_V.reviews(val) : '-',
          }),
      },
      xAxis: {
        data: xAxisData,
        axisLabel: {
          formatter: (value: string) => DBO_EC.timeX(value, dateType),
        },
      },
      yAxis: {
        axisLabel: {
          formatter: DBO_EC.reviews,
        },
      },
      axisPointer: {
        label: {
          formatter: ({ value }: { value: number | string; }) => {
            if (dateType === 'monthly') {
              return DBO_EC.timeX(String(value), dateType);
            }
            return value;
          },
        }
      },
      series: echartSeries,
    };
  }, [series, gameMap, dateType, gameColorMap]);

  return (
    <EChartCommon
      options={options}
      optionsProps={{ type: 'line' }}
      height={height}
      empty={empty}
      emptyTitleText={emptyTitleText}
      hasEmptyDesc={false}
    />
  );
}
